/**
 * The Ordering App's own Item screen (ORDR-165), per `DESIGN/Item.dc.html`.
 * Why the app draws it is at the head of `MenuScreen.tsx`.
 *
 * In order: the item's name and what the chips do not carry, its allergen and
 * dietary chips, the SIZE (required whenever there are portions, and never
 * preselected: ORDR-108), every root group in the item's order with nested
 * groups under their chosen parent, the kitchen note (at most 280) and the
 * quantity.
 *
 * "Add" stays disabled until the size and every visible minimum are met, and
 * its accessible description says why. The figure beside it is a DISPLAY
 * estimate, labelled "Estimated"; the cart shows the server's quote.
 */

import { useId, useState, type ReactElement } from "react";
import { Badge, Button, Input } from "@pirouz-ca/ui-components";
import { formatMinor } from "../customer/money.js";
import type { CatalogueGroup, CatalogueItem } from "./catalogue.js";
import { OptionGroup } from "./OptionGroup.js";
import { buildLine, NOTE_MAX_LENGTH, type OrderLine } from "./orderLine.js";
import { groupKey, initialPicks, lineEstimate, pick, problems, type Picks } from "./selection.js";

export interface ItemScreenProps {
  readonly item: CatalogueItem;
  /** The handle the new cart line takes. */
  readonly lineId: string;
  readonly onAdd: (line: OrderLine) => void;
  readonly onBack: () => void;
}

const MAX_QUANTITY = 99;

export function ItemScreen({ item, lineId, onAdd, onBack }: ItemScreenProps): ReactElement {
  const [portionCode, setPortionCode] = useState<string | null>(null);
  const [picks, setPicks] = useState<Picks>(() => initialPicks(item.groups));
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const reasonsId = useId();
  const noteId = useId();

  const portioned = item.portions.length > 0;
  const portion = item.portions.find((entry) => entry.code === portionCode) ?? null;
  const reasons = problems(item, portionCode, picks);
  const estimate = lineEstimate(item, portionCode, picks, quantity);

  const onPick = (key: string, group: CatalogueGroup, choiceId: string, on: boolean): void => {
    setPicks((current) => pick(current, key, group, choiceId, on));
  };

  return (
    <div className="flex flex-col text-[var(--color-neutral-900)]" data-testid="order-item">
      <div className="px-3.5 pt-3.5">
        <Button type="button" variant="secondary" aria-label="Back to the menu" onClick={onBack} className="h-11 w-11 rounded-full">
          ‹
        </Button>
      </div>
      <div className="flex flex-col gap-4 px-5 py-4">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-2xl font-bold">{item.name}</h1>
          {item.description !== "" && <span className="text-[15px] text-[var(--color-neutral-600)]">{item.description}</span>}
        </div>
        {item.chips.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-[10px] border border-[var(--color-neutral-200)] bg-[var(--color-neutral-50)] px-3.5 py-3">
            <span className="text-sm font-bold">Allergens and dietary</span>
            <span className="flex flex-wrap gap-1.5">
              {item.chips.map((chip) => (
                <Badge key={chip} variant="neutral" size="sm">
                  {chip}
                </Badge>
              ))}
            </span>
          </div>
        )}
        {portioned && (
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="pb-2 text-base font-bold">
              Size <span className="text-sm font-normal text-[var(--color-neutral-600)]">Required</span>
            </legend>
            {item.portions.map((entry) => (
              <label
                key={entry.code}
                className={`flex min-h-12 items-center gap-3 rounded-[10px] px-3.5 text-base ${
                  entry.code === portionCode ? "border-2 border-[var(--color-primary-700)]" : "border border-[var(--color-neutral-300)]"
                }`}
              >
                <input
                  type="radio"
                  name={`${lineId}-size`}
                  checked={entry.code === portionCode}
                  onChange={() => setPortionCode(entry.code)}
                  className="h-5 w-5 accent-[var(--color-primary-700)]"
                />
                <span className="grow">{entry.name}</span>{" "}
                <span className="text-[var(--color-neutral-600)]">{formatMinor(entry.amountMinor, item.currency)}</span>
              </label>
            ))}
          </fieldset>
        )}
        {item.groups.map((group) => (
          <OptionGroup
            key={group.id}
            group={group}
            groupKey={groupKey(null, group.id)}
            picks={picks}
            currency={item.currency}
            portion={portion}
            portioned={portioned}
            onPick={onPick}
          />
        ))}
        <label htmlFor={noteId} className="text-base font-bold">
          Note for the kitchen
        </label>
        <Input
          id={noteId}
          type="text"
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          placeholder="For example: no green onion"
          onChange={(event) => setNote(event.target.value.slice(0, NOTE_MAX_LENGTH))}
        />
      </div>
      <div className="sticky bottom-0 flex flex-col gap-2 border-t border-[var(--color-neutral-200)] bg-[var(--color-neutral-50)] px-5 pt-3 pb-5">
        {reasons.length > 0 && (
          <ul id={reasonsId} className="m-0 list-none p-0 text-sm text-[var(--color-neutral-700)]">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
        {estimate !== null && (
          <p data-testid="line-estimate" className="m-0 text-sm text-[var(--color-neutral-600)]">
            Estimated {formatMinor(estimate, item.currency)}
          </p>
        )}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-xl border border-[var(--color-neutral-300)]">
            <button type="button" aria-label="One fewer" onClick={() => setQuantity((n) => Math.max(1, n - 1))} className="h-12 w-12 text-[22px]">
              −
            </button>
            <span data-testid="quantity" className="min-w-6 text-center text-lg font-bold">
              {quantity}
            </span>
            <button type="button" aria-label="One more" onClick={() => setQuantity((n) => Math.min(MAX_QUANTITY, n + 1))} className="h-12 w-12 text-[22px]">
              +
            </button>
          </div>
          <button
            type="button"
            disabled={reasons.length > 0}
            {...(reasons.length > 0 ? { "aria-describedby": reasonsId } : {})}
            onClick={() => onAdd(buildLine({ id: lineId, item, portionCode, picks, quantity, note }))}
            className="flex min-h-[52px] grow items-center justify-center rounded-xl bg-[var(--color-primary-700)] text-[17px] font-bold text-[var(--color-neutral-50)] disabled:opacity-50"
          >
            {`Add ${String(quantity)} to cart`}
          </button>
        </div>
      </div>
    </div>
  );
}
